import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { Account, AccountType } from "../../../../../shared/domains/account/account";
import { getAvailableBalance } from "../../../../../shared/domains/account/account";
import { useAccounts } from "../../../../../shared/domains/account/use-accounts";
import { useBeneficiaries } from "../../../../../shared/domains/recipients/use-beneficiaries";
import { useExternalAccounts } from "../../../../../shared/domains/recipients/use-external-accounts";
import type {
  AccountOrRecipient,
  PaymentNetworkProduct,
} from "../../../../../shared/domains/transactions/customer-instruction";
import { getDestinationAccounts } from "../../../../../shared/domains/transactions/forex-config-options";
import { AmountText } from "../../../common/amount-text";
import { Avatar } from "../../../common/avatar/avatar";
import { DefaultButton } from "../../../common/buttons/default-button";
import { theme } from "../../../styles/theme";

export enum SourceAndDestinationEnum {
  Source = "source",
  Destination = "destination",
}
interface SourceAndDestinationProps {
  needCreditorAddress?: boolean;
  needCreditorAccountNumber?: boolean;
  needCreditorPhoneNumber?: boolean;
  paymentNetwork?: PaymentNetworkProduct | null;
  type?: SourceAndDestinationEnum | null;
  sourceAccount?: Account | null;
  accountTypeFilter?: AccountType;
  onSourceAccountChange?: (account: Account) => void;
  onDestinationAccountOrRecipientChange?: (account: AccountOrRecipient) => void;
}

interface AccountButtonProps {
  account: AccountOrRecipient;
  onClick?: () => void;
  showAvatar?: boolean;
}

export const AccountButton = (props: AccountButtonProps) => {
  const { account, onClick, showAvatar = true } = props;

  return (
    <StyledButton justified={!!getAvailableBalance(account)} onClick={onClick}>
      {!!account.name && !!getAvailableBalance(account) ? (
        <>
          <div>
            <StyledButtonLeft>{account.name}</StyledButtonLeft>
            <StyledIban>{account.iban}</StyledIban>
          </div>
          {getAvailableBalance(account) ? <StyledAmountText amount={getAvailableBalance(account)} /> : null}
        </>
      ) : (
        <>
          {showAvatar && <AvatarWrapper fontSize="10px" size={24} name={account.name} />}
          <div>
            <AccountNameWrapper>{account.name}</AccountNameWrapper>
            <StyledIban>{account.iban || account.phone || account.accountReference}</StyledIban>
          </div>
        </>
      )}
    </StyledButton>
  );
};

export const SourceAndDestination = (props: SourceAndDestinationProps) => {
  const {
    needCreditorPhoneNumber,
    needCreditorAccountNumber,
    needCreditorAddress,
    paymentNetwork,
    sourceAccount,
    accountTypeFilter,
    type,
    onSourceAccountChange,
    onDestinationAccountOrRecipientChange,
  } = props;
  const allAccounts = useAccounts();
  const accounts = accountTypeFilter ? allAccounts.filter((acc) => acc.type === accountTypeFilter) : allAccounts;

  const { data: externalAccounts } = useExternalAccounts();
  const { data: recipients, canLoadMore, loadMore } = useBeneficiaries();
  const { formatMessage } = useIntl();

  const filteredExternalAccounts = needCreditorPhoneNumber
    ? externalAccounts?.filter((account) => !!account.phone)
    : needCreditorAccountNumber
      ? externalAccounts?.filter((account) => !!account.accountReference)
      : externalAccounts;

  const filteredRecipients = needCreditorPhoneNumber
    ? recipients.filter((recipient) => !!recipient.phone)
    : needCreditorAccountNumber
      ? recipients.filter((recipient) => !!recipient.accountReference)
      : recipients;

  const destinationAccounts =
    paymentNetwork && sourceAccount ? getDestinationAccounts(paymentNetwork, sourceAccount, accounts) : [];

  return type === SourceAndDestinationEnum.Source ? (
    <>
      <StyledLabel>{formatMessage("amountSelection.my_accounts")}</StyledLabel>
      {accounts.map((account, index) => (
        <AccountButtonWrapper key={index}>
          <AccountButton account={account as AccountOrRecipient} onClick={() => onSourceAccountChange?.(account)} />
        </AccountButtonWrapper>
      ))}
    </>
  ) : type === SourceAndDestinationEnum.Destination ? (
    <>
      {destinationAccounts.length > 0 ? (
        <>
          <StyledLabel>{formatMessage("amountSelection.my_accounts")}</StyledLabel>
          {destinationAccounts.map((account, index) => (
            <AccountButtonWrapper key={index}>
              <AccountButton
                account={account as AccountOrRecipient}
                onClick={() => onDestinationAccountOrRecipientChange?.(account as AccountOrRecipient)}
              />
            </AccountButtonWrapper>
          ))}
        </>
      ) : null}
      {filteredExternalAccounts?.length > 0 ? (
        <>
          <StyledLabel>{formatMessage("amountSelection.my_external_account")}</StyledLabel>
          {filteredExternalAccounts.map((account, index) => (
            <AccountButtonWrapper key={index}>
              <AccountButton
                account={account as AccountOrRecipient}
                onClick={() => onDestinationAccountOrRecipientChange?.(account as AccountOrRecipient)}
                showAvatar={false}
              />
            </AccountButtonWrapper>
          ))}
        </>
      ) : null}
      {filteredRecipients?.length > 0 ? (
        <>
          <StyledLabel>{formatMessage("amountSelection.my_recipients")}</StyledLabel>
          {filteredRecipients.map((account, index) => (
            <AccountButtonWrapper key={index}>
              <AccountButton
                account={account as AccountOrRecipient}
                onClick={() => onDestinationAccountOrRecipientChange?.(account as AccountOrRecipient)}
              />
            </AccountButtonWrapper>
          ))}
          {canLoadMore ? (
            <DefaultButton onClick={loadMore}>{formatMessage("amountSelection.load_more")}</DefaultButton>
          ) : null}
        </>
      ) : null}
    </>
  ) : null;
};

const StyledButton = styled("div")<{ justified: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  box-shadow: none;
  background-color: white;
  text-align: left;
  padding: 15px;
  border-radius: 10px;
  cursor: pointer;
  min-width: 0;
  ${theme.bodyBlackRegular};
  ${(props) => (props.justified ? "justify-content: space-between;" : "")}
`;

const AccountButtonWrapper = styled.div`
  margin-bottom: 15px;
`;

const StyledButtonLeft = styled.span`
  white-space: wrap;
  margin-right: 10px;
`;

const StyledAmountText = styled(AmountText)`
  ${theme.boldText};
`;

const StyledLabel = styled.p`
  margin: 30px 0 5px;
  ${theme.mediumText};
`;

const StyledIban = styled.span`
  display: block;
  ${theme.bodyGreyRegular};
  font-size: 12px;
`;

const AvatarWrapper = styled(Avatar)`
  margin-right: 10px;
`;

const AccountNameWrapper = styled.div`
  text-transform: capitalize;
`;
